import {isAccessLeaseLapsed} from './sessionAccess';

// One silent recovery per window. A permission-denied on a live listener
// while the user is STILL signed in is almost always the wake-from-sleep
// race: Safari suspends the tab, the SDK's proactive token-refresh timer
// never fires, and on wake the Watch stream reconnects with an expired ID
// token — the server rejects it before the SDK refreshes (JRFD-LOG-P/Z hit
// this daily). A second denial inside the window means the fresh token was
// genuinely denied by the rules, which IS worth reporting.
const PERMISSION_RETRY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Binds the Firestore listener error helpers to an app's Firebase Auth
 * instance and reportError. Both apps use this, so the sign-out race and the
 * wake-from-sleep recovery are decided the same way in each (#2050).
 *
 * @param {Object} options
 * @param {Object} options.auth - the app's Firebase Auth instance; read at
 *   error time, so a test mock can toggle currentUser
 * @param {Function} options.reportError - (error, context) => void
 * @param {Function} [options.isAccessLapsed] - true while the session access
 *   lease has lapsed; defaults to the running SessionAccessGate controller's
 * @returns {{snapshotErrorHandler: Function, resilientSnapshot: Function}}
 */
export const createSnapshotErrorReporting = ({auth, reportError, isAccessLapsed = isAccessLeaseLapsed}) => {
    /**
     * Builds an error callback for a Firestore onSnapshot listener.
     *
     * A permission-denied while auth is tearing down is the sign-out /
     * token-expiry race — the listener is still attached while the session is
     * torn down, just before the app redirects to /login — not a real
     * authorization failure, so it's swallowed rather than reported (#1034,
     * #1153). signOut() nulls auth.currentUser locally before the stream errors,
     * which is how the two cases are told apart.
     *
     * A permission-denied while the session access lease has lapsed is
     * swallowed without resubscribing: a device that slept past its lease wakes
     * with every protected listener denied at once, and SessionAccessGate
     * unmounts and remounts them all once renewal succeeds. Resubscribing
     * before then would only be denied again (JRFD-LOG-P, LIONSBAYFD-6).
     *
     * A permission-denied while currentUser IS set gets one silent recovery per
     * window when a `resubscribe` callback is provided: onSnapshot kills the
     * listener on this error, so we force a token refresh and hand the caller
     * this same handler to attach a replacement listener (see the window
     * constant above for why). Only a repeat denial inside the window — the
     * rules denying a fresh token, i.e. a real rules-vs-claims mismatch or
     * rules regression — is reported. Without `resubscribe` it reports
     * immediately, as before. Everything reported goes to Sentry tagged with
     * `context` so the event self-identifies instead of joining the generic
     * "Missing or insufficient permissions" bucket.
     *
     * @param {string} context - '<Component>.<listener>' identifier for Sentry
     * @param {Function} [onError] - invoked for every error, including swallowed
     *   permission-denied — use for UI fallbacks (clear rows, stop spinners)
     * @param {Function} [resubscribe] - called with this handler after the token
     *   refresh; must attach a fresh onSnapshot using it as the error callback
     * @returns {Function} error callback to pass as onSnapshot's third argument
     */
    const snapshotErrorHandler = (context, onError, resubscribe) => {
        let lastRetryAt = 0;

        const handler = error => {
            if (onError) {
                onError(error);
            }

            if (error?.code === 'permission-denied') {
                if (!auth.currentUser || isAccessLapsed()) {
                    return;
                }

                const now = Date.now();
                if (resubscribe && now - lastRetryAt > PERMISSION_RETRY_WINDOW_MS) {
                    lastRetryAt = now;
                    // Best-effort refresh: if it fails (network not back yet
                    // after wake), resubscribe anyway — the new stream pulls its
                    // token from the auth credential provider, which refreshes
                    // expired tokens itself.
                    return Promise.resolve()
                        .then(() => auth.currentUser?.getIdToken(true))
                        .catch(() => {})
                        .then(() => resubscribe(handler));
                }
            }

            reportError(error, {context});
        };

        return handler;
    };

    /**
     * Attaches a Firestore listener that survives the wake-from-sleep
     * permission-denied race by resubscribing once per window (see
     * snapshotErrorHandler). Use for long-lived listeners a user may hold open
     * across a sleep/wake cycle — without this, onSnapshot leaves them dead
     * (stale data) until the next full page load.
     *
     * @param {Function} subscribe - (errorCallback) => unsubscribe; must attach
     *   a fresh onSnapshot with the given error callback and return its
     *   unsubscribe function
     * @param {string} context - '<Component>.<listener>' identifier for Sentry
     * @param {Function} [onError] - see snapshotErrorHandler
     * @returns {Function} unsubscribe for whichever listener is currently attached
     */
    const resilientSnapshot = (subscribe, context, onError) => {
        let disposed = false;
        let unsubscribe = () => {};

        const handler = snapshotErrorHandler(context, onError, retryHandler => {
            if (disposed) {
                return;
            }
            // The errored listener is already dead server-side; release the
            // client-side registration before replacing it.
            unsubscribe();
            unsubscribe = subscribe(retryHandler);
        });

        unsubscribe = subscribe(handler);

        return () => {
            disposed = true;
            unsubscribe();
        };
    };

    return {snapshotErrorHandler, resilientSnapshot};
};
